import { supabase } from '@/integrations/supabase/client';

// Кэш для настройки логирования
let detailedLoggingEnabled: boolean | null = null;
let lastCheck = 0;
const CACHE_DURATION = 30000; // 30 секунд

/**
 * Получает настройку детального логирования из базы данных
 */
const getDetailedLoggingEnabled = async (): Promise<boolean> => {
  const now = Date.now();
  
  // Используем кэш для избежания частых запросов к БД
  if (detailedLoggingEnabled !== null && (now - lastCheck) < CACHE_DURATION) {
    return detailedLoggingEnabled;
  }

  try {
    const { data } = await supabase
      .from('env_variables')
      .select('value')
      .eq('key', 'ENABLE_DETAILED_LOGGING')
      .single();
    
    detailedLoggingEnabled = data?.value === 'true';
    lastCheck = now;
    
    return detailedLoggingEnabled;
  } catch (error) {
    // В случае ошибки возвращаем false (логи отключены)
    return false;
  }
};

/**
 * Сброс кэша (используется при изменении настроек)
 */
export const clearLoggingCache = () => {
  detailedLoggingEnabled = null;
  lastCheck = 0;
};

/**
 * Логирование отладочной информации (контролируется настройкой)
 */
export const logDebug = async (message: string, ...args: any[]) => {
  const enabled = await getDetailedLoggingEnabled();
  if (enabled) {
    console.log(`🐛 ${message}`, ...args);
  }
};

/**
 * Информационное логирование (контролируется настройкой)
 */
export const logInfo = async (message: string, ...args: any[]) => {
  const enabled = await getDetailedLoggingEnabled();
  if (enabled) {
    console.info(`ℹ️ ${message}`, ...args);
  }
};

/**
 * Логирование предупреждений (всегда включено)
 */
export const logWarn = (message: string, ...args: any[]) => {
  console.warn(`⚠️ ${message}`, ...args);
};

/**
 * Логирование ошибок (всегда включено)
 */
export const logError = (message: string, ...args: any[]) => {
  console.error(`❌ ${message}`, ...args);
};

/**
 * Условное логирование для development
 */
export const logDev = async (message: string, ...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    const enabled = await getDetailedLoggingEnabled();
    if (enabled) {
      console.log(`🔧 ${message}`, ...args);
    }
  }
};

/**
 * Логирование с проверкой настройки (синхронная версия для срочных случаев)
 */
export const logSync = (level: 'debug' | 'info' | 'warn' | 'error', message: string, ...args: any[]) => {
  const prefix = {
    debug: '🐛',
    info: 'ℹ️', 
    warn: '⚠️',
    error: '❌'
  }[level];

  // Warn и error всегда показываем
  if (level === 'warn' || level === 'error') {
    console[level](`${prefix} ${message}`, ...args);
    return;
  }

  // Для debug и info проверяем кэш (без await)
  if (detailedLoggingEnabled === true) {
    console[level === 'debug' ? 'log' : 'info'](`${prefix} ${message}`, ...args);
  }
};

export default {
  debug: logDebug,
  info: logInfo,
  warn: logWarn,
  error: logError,
  dev: logDev,
  sync: logSync,
  clearCache: clearLoggingCache
};